import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';

import { fetchSettings } from '../../../store/modules/subcategory/subcategoryActions';
import useFormSettings from '../../../hooks/useFormSettings';

import categoryService from '../../../services/categoryService';
import { useHasPermission } from '../../../utils/permissionUtils';

const SubCategoryForm = ({ initialData, onSave, isLoading, onClose }) => {
    const hasCreatePermission = useHasPermission(['create sub categories']);
    const hasUpdatePermission = useHasPermission(['update sub categories']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('subcategory', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        category: null,
        name: {},
        description: {},
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                category: initialData.category || null,
                name: initialData.name || {},
                description: initialData.description || {}
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectChange = async (name, value) => {
        if(name === 'category') {
            setFormData(prev => ({
                ...prev,
                [name]: value ?? null,
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-12">
                        <CommonAsyncSelect
                            name="category"
                            label="Category"
                            value={formData.category}
                            onChange={(value) => handleSelectChange('category', value)}
                            loadOptions={async (inputValue) => {
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'equal',
                                                    rule: 'categories.type',
                                                    type: 'text',
                                                    value: 'item'
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await categoryService.getList({ search: inputValue }, condition);
                                return response.data.map(category => ({
                                    id: category.id,
                                    label: category.name?.en,
                                }));
                            }}
                            isSubmitting={isSubmitting || initialData?.initialType === 'create'}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-12">
                        <TranslatableInput
                            label="SubCategory Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-12">
                        <TranslatableTextarea
                            label="Description"
                            name="description"
                            rows={1}
                            value={formData.description}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default SubCategoryForm; 